/**
 * Ruby Ledger — pure formatting only (plan Task 17). No fetches, no React.
 *
 * `mathLines` renders the drawer's "Vendor tokens spent → Ruby Tokens
 * charged" block: one line per vendor attempt (billed even when it failed —
 * ruling R19), the vendor total, the multiplier step that produces the exact
 * (unrounded) Ruby Token figure, the rounding to a whole credit (or the
 * failed/refunded outcome), what was held vs released, and the derived
 * profit. All money is formatted to the smallest unit that is still exact —
 * never fewer than 2 decimal places, never more than 4.
 */

export interface LedgerMoneyLike {
  vendorCostUsd?: number;
  multiplier: number;
  multiplierSource: string;
  rubyExact?: number;
  tokensReserved?: number;
  tokensCharged?: number;
  tokensRefunded?: number;
  chargedUsd?: number;
  profitUsd?: number;
  /** An admin test / free run — the vendor was still paid, but never billed to the merchant. */
  free?: boolean;
}

export interface LedgerAttemptLike {
  step: string;
  providerId?: string;
  modelId: string;
  modelVersion?: string;
  unit: string;
  quantity: number;
  unitPriceUsd: number;
  costUsd: number;
  ok: boolean;
  error?: string;
}

export interface LedgerRunLike extends LedgerMoneyLike {
  attempts?: LedgerAttemptLike[];
  /** Present on a failed run; code REFUND_FAILED means the hold was never returned. */
  error?: { code?: string } | null;
}

const UNIT_LABEL: Record<string, [string, string]> = {
  input_token: ['input token', 'input tokens'],
  output_token: ['output token', 'output tokens'],
  image: ['image', 'images'],
  second: ['second', 'seconds'],
  call: ['call', 'calls'],
  mixed: ['unit', 'units'],
};

function unitLabel(unit: string, n: number): string {
  const pair = UNIT_LABEL[unit] ?? ['unit', 'units'];
  return pair[n === 1 ? 0 : 1];
}

/**
 * $ amount at the smallest precision that is still exact, clamped to
 * 2..4 decimal places — "$0.07", "$0.0387", never "$0.0700" or "$0".
 */
export function money(n: number): string {
  if (!Number.isFinite(n)) return '$0.00';
  const abs = Math.abs(n);
  let digits = abs.toFixed(4).replace(/0+$/, '');
  if (digits.endsWith('.')) digits += '00';
  else if (digits.split('.')[1]?.length === 1) digits += '0';
  return `${n < 0 ? '-' : ''}$${digits}`;
}

function int(n: number | undefined): string {
  return Math.round(n ?? 0).toLocaleString('en-US');
}

/** Price shown the way the vendor billed it — per-million for tokens, per-unit otherwise. */
function priceText(unit: string, unitPriceUsd: number): string {
  if (unit === 'input_token' || unit === 'output_token') {
    return `$${(unitPriceUsd * 1e6).toFixed(2)}/1M`;
  }
  return `${money(unitPriceUsd)} each`;
}

/** One attempt line: "step · modelId vVersion — qty unit × price = $cost", or "… — failed (reason)". */
export function attemptLine(a: LedgerAttemptLike): string {
  const ver = a.modelVersion ? ` v${a.modelVersion}` : '';
  const head = `${a.step} · ${a.modelId}${ver}`;
  if (!a.ok) return `${head} — failed (${a.error ?? 'unknown error'})`;
  const qty = a.quantity.toLocaleString('en-US');
  return `${head} — ${qty} ${unitLabel(a.unit, a.quantity)} × ${priceText(a.unit, a.unitPriceUsd)} = ${money(a.costUsd)}`;
}

/**
 * The full "Vendor tokens spent → Ruby Tokens charged" math block for the
 * drawer, in display order. A failed/refunded run (tokensCharged === 0 with
 * something refunded) collapses the rounding + charge lines into the one
 * outcome line the mockup specifies: "0 charged, N returned". A free run
 * (admin test / `free: true`) is neither — the vendor was paid but nothing
 * was ever reserved or returned, so it gets its own line rather than being
 * read as a failure.
 */
/** A multiplier as a short exact number: 1.5, 1.25, 0.8333 — at most 4 decimals. */
function rateText(n: number): string {
  return String(Math.round(n * 10000) / 10000);
}

export function mathLines(run: LedgerRunLike): string[] {
  const lines: string[] = [];
  const attempts = run.attempts ?? [];
  for (const a of attempts) lines.push(attemptLine(a));

  lines.push(`We paid the vendors ${money(run.vendorCostUsd ?? 0)}`);

  // Only ACCEPTED attempts are billable (settle's billableCostUsd): a failed
  // attempt is paid to the vendor but never charged. The exact figure is
  // computed from that base, at the rate the run actually recorded — a
  // tenant override can differ from the action/vibe multiplier, so the rate is
  // read back from rubyExact when both are present.
  const base = attempts.length > 0
    ? attempts.filter((a) => a.ok).reduce((sum, a) => sum + (a.costUsd > 0 ? a.costUsd : 0), 0)
    : (run.vendorCostUsd ?? 0);
  const recordedRate = base > 0 && (run.rubyExact ?? 0) > 0 ? (run.rubyExact as number) / (base * 100_000) : run.multiplier;
  const rate = Number.isFinite(recordedRate) && recordedRate > 0 ? recordedRate : run.multiplier;
  const exact = base * rate * 100_000;
  const rateNote = Math.abs(rate - run.multiplier) > 1e-6
    ? `${run.multiplierSource} rate; list ${rateText(run.multiplier)}`
    : run.multiplierSource;
  lines.push(`Accepted attempts ${money(base)} × ${rateText(rate)} (${rateNote}) × 100,000 = ${int(exact)} Ruby Tokens value`);

  const charged = Math.round(run.tokensCharged ?? 0);
  const refunded = Math.round(run.tokensRefunded ?? 0);
  const refundFailed = run.error?.code === 'REFUND_FAILED';
  if (run.free) {
    lines.push(`${int(exact)} Ruby Tokens value → charged 0 (Free AI)`);
  } else if (charged > 0) {
    lines.push(`Rounded up to a whole credit — ${int(charged)} Ruby Tokens`);
    lines.push(`Charged ${int(charged)} Ruby Tokens = ${money(run.chargedUsd ?? 0)}`);
  } else if (refundFailed) {
    lines.push('0 charged — the refund FAILED, the hold is still on the wallet');
  } else {
    lines.push(`${charged} charged, ${int(refunded)} returned`);
  }

  if (!run.free) {
    const reserved = Math.round(run.tokensReserved ?? 0);
    if (refundFailed) {
      lines.push(`Held ${int(reserved)} · NOT released (refund failed)`);
    } else {
      const released = Math.max(0, reserved - charged - refunded);
      lines.push(`Held ${int(reserved)} · released ${int(released)}`);
    }
  }

  const profit = run.profitUsd ?? 0;
  lines.push(`Profit ${profit < 0 ? '-' : '+'}${money(Math.abs(profit))}`);

  return lines;
}

// ---------------------------------------------------------------------------
// Table-cell formatting

export interface LedgerLineLike {
  createdAt: string;
  tenantId: string;
  tenantName?: string;
  locationId?: string;
  locationName?: string;
  source?: { surface: string; path?: string };
  rubyExact?: number;
  free?: boolean;
  keepBackground?: boolean;
  actionId: string;
  vibeId?: string | null;
  target?: { type?: string; id?: string; name?: string };
  status: string;
  settlement?: string;
  providerId?: string;
  modelId?: string;
  modelVersion?: string;
  vendorSpent?: string;
  vendorCostUsd?: number;
  multiplier?: number;
  tokensCharged?: number;
  tokensRefunded?: number;
  chargedUsd?: number;
  profitUsd?: number;
  feedback?: { rating: 'good' | 'bad' } | null;
}

/** "Oct 4, 9:50 PM" — local time, short enough for a table cell. */
export function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

/** The action id, with its vibe in parens when one was used. */
export function actionLabel(line: Pick<LedgerLineLike, 'actionId' | 'vibeId'>): string {
  return line.vibeId ? `${line.actionId} (${line.vibeId})` : line.actionId;
}

export function itemLabel(target: LedgerLineLike['target']): string {
  if (!target) return '';
  return target.name || `${target.type ?? ''}:${target.id ?? ''}`;
}

export function modelLabel(line: Pick<LedgerLineLike, 'providerId' | 'modelId'>): string {
  return line.providerId && line.modelId ? `${line.providerId} · ${line.modelId}` : '';
}

/** "succeeded" alone, or "failed / refunded" when a settlement adds information. */
export function statusLabel(line: Pick<LedgerLineLike, 'status' | 'settlement'>): string {
  return line.settlement && line.settlement !== 'none' ? `${line.status} / ${line.settlement}` : line.status;
}

export function ratingLabel(line: Pick<LedgerLineLike, 'feedback'>): string {
  if (line.feedback?.rating === 'good') return '👍';
  if (line.feedback?.rating === 'bad') return '👎';
  return '—';
}

export const profitTone = (profitUsd: number): 'positive' | 'negative' =>
  profitUsd < 0 ? 'negative' : 'positive';

// ---------------------------------------------------------------------------
// Overview tiles

export interface LedgerTotalsLike {
  runs: number;
  vendorCostUsd: number;
  tokensCharged: number;
  chargedUsd: number;
  profitUsd: number;
  refundedRuns: number;
  good: number;
  bad: number;
}

/** "👍 67%" from good/bad feedback counts — "—" when nobody has rated yet. */
export function thumbsUpRate(totals: Pick<LedgerTotalsLike, 'good' | 'bad'>): string {
  const total = totals.good + totals.bad;
  if (total === 0) return '—';
  return `${Math.round((totals.good / total) * 100)}%`;
}

export interface LedgerTenantMargin {
  tenantId: string;
  runs: number;
  chargedUsd: number;
  vendorCostUsd: number;
  profitUsd: number;
}

/**
 * Per-tenant charged / vendor cost / profit from ledger lines (RUN rows) —
 * the figures the Overview margin lists use. Spend rows carry no charge for
 * engine runs, so margin must come from here, never from the spend pivot.
 */
export function tenantMarginsFromLedger(
  lines: Array<Pick<LedgerLineLike, 'tenantId' | 'chargedUsd' | 'vendorCostUsd' | 'profitUsd'>>,
): LedgerTenantMargin[] {
  const by = new Map<string, LedgerTenantMargin>();
  for (const l of lines) {
    const m = by.get(l.tenantId) ?? { tenantId: l.tenantId, runs: 0, chargedUsd: 0, vendorCostUsd: 0, profitUsd: 0 };
    m.runs += 1;
    m.chargedUsd += l.chargedUsd ?? 0;
    m.vendorCostUsd += l.vendorCostUsd ?? 0;
    m.profitUsd += l.profitUsd ?? 0;
    by.set(l.tenantId, m);
  }
  return [...by.values()];
}

export { money as formatLedgerMoney };

// ---------------------------------------------------------------------------
// Merchant / where / value (follow-up 2026-10-05)

/** The business name, falling back to the raw tenant id. */
export function merchantName(line: Pick<LedgerLineLike, 'tenantId' | 'tenantName'>): string {
  return line.tenantName || line.tenantId;
}

/** The run's location: its name, else its raw id, else ''. */
export function locationLabel(line: Pick<LedgerLineLike, 'locationId' | 'locationName'>): string {
  return line.locationName || line.locationId || '';
}

/** Where the run was started — the surface label, or '—' for runs from before it was recorded. */
export function whereLabel(line: Pick<LedgerLineLike, 'source'>): string {
  return line.source?.surface || '—';
}

/** The full × multiplier value of a run in Ruby Tokens (rubyExact, unrounded → whole tokens). */
export function rubyValueTokens(line: Pick<LedgerLineLike, 'rubyExact'>): number {
  return Math.round(line.rubyExact ?? 0);
}

export interface FilterOption { value: string; label: string; subtitle?: string }

/** Sentinel option value for "All" in the custom dropdowns (SearchableDropdown needs a non-empty value). */
export const ALL = '__all';

/**
 * Action filter options: "All actions", every catalog action, then any id seen
 * in the loaded rows the catalog doesn't know (e.g. `legacy.*` inline runs).
 */
export function actionFilterOptions(
  catalog: Array<{ actionId: string; label?: string }>,
  lines: Array<Pick<LedgerLineLike, 'actionId'>>,
): FilterOption[] {
  const out: FilterOption[] = [{ value: ALL, label: 'All actions' }];
  const seen = new Set<string>();
  for (const a of catalog) {
    if (!a.actionId || seen.has(a.actionId)) continue;
    seen.add(a.actionId);
    out.push({ value: a.actionId, label: a.label || a.actionId, subtitle: a.label && a.label !== a.actionId ? a.actionId : undefined });
  }
  const extra = [...new Set(lines.map((l) => l.actionId).filter((id) => id && !seen.has(id)))].sort();
  for (const id of extra) out.push({ value: id, label: id, subtitle: id.startsWith('legacy.') ? 'legacy' : undefined });
  return out;
}

/** Model filter options: "All models", then "provider · model" from the catalog and the loaded rows (filter value = modelId). */
export function modelFilterOptions(
  catalog: Array<{ providerId: string; modelId: string }>,
  lines: Array<Pick<LedgerLineLike, 'providerId' | 'modelId'>>,
): FilterOption[] {
  const out: FilterOption[] = [{ value: ALL, label: 'All models' }];
  const seen = new Set<string>();
  const add = (providerId: string | undefined, modelId: string | undefined) => {
    if (!modelId || seen.has(modelId)) return;
    seen.add(modelId);
    out.push({ value: modelId, label: providerId ? `${providerId} · ${modelId}` : modelId });
  };
  for (const m of catalog) add(m.providerId, m.modelId);
  for (const l of lines) add(l.providerId, l.modelId);
  return out;
}

// ---------------------------------------------------------------------------
// Period + paging (Ledger redesign 2026-10-05)

export type LedgerPeriodPreset = 'today' | 'yesterday' | '7d' | 'this_month' | 'custom';

export interface LedgerPeriod {
  preset: LedgerPeriodPreset;
  /** Custom range only — local calendar days, YYYY-MM-DD, inclusive. */
  fromDay?: string;
  toDay?: string;
}

export const PERIOD_PRESETS: Array<{ preset: LedgerPeriodPreset; label: string }> = [
  { preset: 'today', label: 'Today' },
  { preset: 'yesterday', label: 'Yesterday' },
  { preset: '7d', label: 'Last 7 days' },
  { preset: 'this_month', label: 'This month' },
  { preset: 'custom', label: 'Custom range' },
];

const startOfLocalDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

function parseDay(day: string | undefined): Date | null {
  if (!day || !/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** YYYY-MM-DD of a local date. */
export function dayKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * The period's window in ISO (UTC) instants, using the admin's local days:
 * Today = local midnight → now; Yesterday = the whole local day before;
 * Last 7 days = today and the 6 days before it; This month = the 1st → now;
 * Custom = fromDay 00:00 → toDay 23:59:59.999 (swapped if reversed).
 */
export function resolvePeriod(p: LedgerPeriod, now: Date = new Date()): { fromIso: string; toIso: string } {
  const today = startOfLocalDay(now);
  const iso = (d: Date) => d.toISOString();
  switch (p.preset) {
    case 'yesterday': {
      const from = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
      return { fromIso: iso(from), toIso: iso(new Date(today.getTime() - 1)) };
    }
    case '7d':
      return { fromIso: iso(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 6)), toIso: iso(now) };
    case 'this_month':
      return { fromIso: iso(new Date(today.getFullYear(), today.getMonth(), 1)), toIso: iso(now) };
    case 'custom': {
      let a = parseDay(p.fromDay) ?? today;
      let b = parseDay(p.toDay) ?? a;
      if (a > b) [a, b] = [b, a];
      const end = new Date(b.getFullYear(), b.getMonth(), b.getDate() + 1);
      return { fromIso: iso(a), toIso: iso(new Date(end.getTime() - 1)) };
    }
    case 'today':
    default:
      return { fromIso: iso(today), toIso: iso(now) };
  }
}

function shortDay(day: string | undefined): string {
  const d = parseDay(day);
  return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '…';
}

/** "today", "yesterday", "the last 7 days", "this month", "Oct 1 – Oct 4" — reads after "Show runs for". */
export function periodPhrase(p: LedgerPeriod): string {
  switch (p.preset) {
    case 'yesterday': return 'yesterday';
    case '7d': return 'the last 7 days';
    case 'this_month': return 'this month';
    case 'custom':
      return p.fromDay && p.toDay && p.fromDay !== p.toDay
        ? `${shortDay(p.fromDay)} – ${shortDay(p.toDay)}`
        : shortDay(p.fromDay ?? p.toDay);
    case 'today':
    default: return 'today';
  }
}

/** Ledger page size for the transactions list. */
export const LEDGER_PAGE_SIZE = 25;

/**
 * Client-side cursor stack over the server's forward-only cursor:
 * `cursors[i]` is the cursor that loads page i (page 0 = undefined),
 * `counts[i]` how many rows page i held once loaded.
 */
export interface LedgerPager {
  page: number;
  cursors: Array<string | undefined>;
  counts: number[];
}

export const firstPage = (): LedgerPager => ({ page: 0, cursors: [undefined], counts: [] });

/** Move to the next page, remembering the cursor that loads it. */
export function nextPage(p: LedgerPager, nextCursor: string | undefined): LedgerPager {
  if (!nextCursor) return p;
  const cursors = p.cursors.slice(0, p.page + 1);
  cursors.push(nextCursor);
  return { page: p.page + 1, cursors, counts: p.counts.slice(0, p.page + 1) };
}

export function prevPage(p: LedgerPager): LedgerPager {
  return p.page === 0 ? p : { ...p, page: p.page - 1 };
}

export function recordPageCount(p: LedgerPager, n: number): LedgerPager {
  const counts = p.counts.slice(0, p.page);
  while (counts.length < p.page) counts.push(0);
  counts.push(n);
  return { ...p, counts };
}

/** 1-based index range of the rows on the current page, and how many rows shown so far. */
export function pageRange(p: LedgerPager, rowsOnPage: number): { first: number; last: number; shownSoFar: number } {
  const before = p.counts.slice(0, p.page).reduce((a, b) => a + b, 0);
  return { first: rowsOnPage ? before + 1 : before, last: before + rowsOnPage, shownSoFar: before + rowsOnPage };
}

/** "google_vertex · gemini-2.5-flash-image" → "gemini-2.5-flash-image" (the grid's short model). */
export function shortModel(line: Pick<LedgerLineLike, 'modelId'>): string {
  return line.modelId ?? '';
}

/** Rating "👍 3 · 👎 1". */
export function ratingCounts(t: Pick<LedgerTotalsLike, 'good' | 'bad'>): string {
  return `👍 ${t.good} · 👎 ${t.bad}`;
}
